import React, { useContext, useEffect } from 'react'
import TextButton from 'LuxKit/Button/TextButton'
import BedbankTwinShare from 'components/OfferList/OfferListTiles/Support/BedbankTwinShare'
import BedbankMarginText from 'components/OfferPage/Bedbank/BedbankMargin'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import OfferListEventsContext, {
  OfferListEvents,
} from 'components/OfferList/OfferListEventsContext'
import PayAtPropertyPill from 'components/Common/PayAtPropertyPill'
import Group from 'components/utils/Group'
import { offerIncludesPayAtProperty } from 'components/OfferPage/utils'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import { pluralizeToString } from 'lib/string/pluralize'
import { buildSuggestedDatesString } from 'lib/search/searchUtils'

interface Props {
  offer: App.BedbankOffer | App.BedbankOfferSummary
  offerPrice: number
  isFlexibleSearch?: string
  suggestedDates?: {
    checkIn: string
    checkOut: string
  }
  nightsToDisplay: number
  promoteAsBundle: boolean
  saleUnit: string
  rooms: Array<App.Occupants>
  discountPercentage: number
  value: number
  hotelNights: number
  offerLocations: Array<string>
  displayMargin?: boolean
  margin?: number
  marginAud?: number
  supplier?: string
  directSearchPrices?: App.OfferListMetadataPricing
  shownRate?: {
    mobilePromotion?: boolean
  }
  bundlePropertyFees?: number
  propertyFees?: number
  bundleTaxesAndFees?: number
  taxesAndFees?: number
  bundlePrice: number
  hotelPrice: number
  flightPrice?: number
  offerUrl: string
  hidePrice?: boolean
}

function BedbankMetaSearchTileGarden({
  offer,
  offerPrice,
  isFlexibleSearch,
  suggestedDates,
  nightsToDisplay,
  promoteAsBundle,
  saleUnit,
  rooms,
  hotelNights,
  offerLocations,
  displayMargin,
  margin,
  marginAud,
  supplier,
  directSearchPrices,
  shownRate,
  bundlePropertyFees,
  propertyFees,
  bundleTaxesAndFees,
  taxesAndFees,
  bundlePrice,
  hotelPrice,
  flightPrice,
  offerUrl,
  hidePrice = false,
}: Props) {
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  useEffect(() => {
    dispatchOfferListEvent({
      type: OfferListEvents.pricingCalculated,
      availability: true,
      roomRateId: undefined,
      price: hidePrice ? undefined : offerPrice,
      memberPrice: undefined,
      duration: nightsToDisplay,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const _propertyFees = promoteAsBundle ? bundlePropertyFees : propertyFees
  const _hotelPrice = promoteAsBundle ? bundlePrice : hotelPrice

  if (hidePrice) {
    return null
  }

  return (
    <>
      {!!offerPrice && <>
        <Group direction="vertical" gap={8} horizontalAlign="start">
          {offerIncludesPayAtProperty(offer) && (
            <PayAtPropertyPill />
          )}
        </Group>
        <PriceRowPriceCaption>
          {isFlexibleSearch && suggestedDates ?
              buildSuggestedDatesString(suggestedDates.checkIn, suggestedDates.checkOut) :
              pluralizeToString('night', nightsToDisplay)}
          {promoteAsBundle && ' + flights'}
          {' '}from
        </PriceRowPriceCaption>
        <LuxPlusPriceStack
          size="L"
          price={offerPrice}
          saleUnit={saleUnit}
        />
        {offer.displayPricingAsPerPerson && <BedbankTwinShare rooms={rooms} />}
      </>}

      <PriceRowAgentHubCommission
        productType={offer.productType}
        offerId={offer.id}
        offerLocations={offerLocations}
      />

      {displayMargin && (
        <BedbankMarginText
          margin={margin!}
          marginAud={marginAud!}
          supplier={supplier!}
          type="bedbank_estimate"
        />
      )}
      {(directSearchPrices || (shownRate && hotelNights)) && (
        <OfferPriceDetailsRow
          trigger="price-row"
          triggerSize="M"
          offer={offer}
          rooms={rooms}
          propertyFees={_propertyFees}
          taxesAndFees={promoteAsBundle ? bundleTaxesAndFees : taxesAndFees}
          hotelPrice={_hotelPrice - (_propertyFees ?? 0)}
          flightPrice={promoteAsBundle ? flightPrice : undefined}
          duration={hotelNights}
          cta={
            <TextButton kind="primary" fit="flex" to={offerUrl}>
              View Offer
            </TextButton>
          }
          context="search"
          hasMobilePromotion={shownRate?.mobilePromotion}
        />
      )}
    </>
  )
}

export default BedbankMetaSearchTileGarden
